import {
  SESSION_STORAGE_KEYS,
  getSessionStorageItem,
  removeSessionStorageItem,
  setSessionStorageItem,
} from '@chegg-wt/core/utils/sessionStorage'
import { CreateProject } from '@chegg-wt/core/state/currentProject/types'
import { FormType } from '../constants/form'
import { NEW_PROJECT_ID } from '../constants/projects'
import { AppRouteParams, AppRouteCreators } from '../types/core'
import { CitationData, UpgradeCitationObject } from '../types/form'
import { trackWithoutThrow } from './errors'
import { getCitationsFromSessionStorage } from './writingForms'

interface FormSession {
  data: CitationData
  route: {
    formType: FormType
    params: AppRouteParams
  }
}

export const getSessionFormData = (): FormSession => {
  try {
    return {
      data: JSON.parse(getSessionStorageItem(SESSION_STORAGE_KEYS.formData)),
      route: JSON.parse(getSessionStorageItem(SESSION_STORAGE_KEYS.formRoute)),
    }
  } catch (error) {
    return {
      data: null,
      route: null,
    }
  }
}

export const afterUpgradingRoute = (
  routes: AppRouteCreators,
  formData: FormSession = getSessionFormData(),
  hasPremiumFlow = false
) => {
  const { data, route } = formData

  const isParamsAvailable = route?.params && Object.keys(route.params).length > 0
  const isFormSessionAvailable = data && isParamsAvailable
  const isProjectIdAvailable = route?.params?.projectId

  if (hasPremiumFlow) {
    if (!isFormSessionAvailable) {
      return routes.toDashboard()
    }
    if (!isProjectIdAvailable) {
      return routes.toCitationListForm({ ...route.params, projectId: NEW_PROJECT_ID })
    }
    return routes.toCitationListForm(route.params)
  }

  if (!isFormSessionAvailable) {
    return routes.toFolders()
  }

  const { formType, params } = route
  switch (formType) {
    case 'new':
      return routes.toNewCitation(params)
    case 'edit':
      return routes.toEditCitation(params)
    case 'manual':
      return routes.toManualCitation(params)
    default:
      return routes.toFolders()
  }
}

export const onClickNoThanks = () => {
  window.history.back()
}

export const getUpgradeCitationsFromSessionStorage = (): UpgradeCitationObject | null => {
  const upgradeCitationObject = getSessionStorageItem(
    SESSION_STORAGE_KEYS.upgradeCitationObject
  )
  if (!upgradeCitationObject) return null

  try {
    return JSON.parse(upgradeCitationObject)
  } catch (e) {
    trackWithoutThrow(e)
  }
}

export const setUpgradeCitationInSessionStorage = (
  upgradeCitation: UpgradeCitationObject
) => {
  setSessionStorageItem(
    SESSION_STORAGE_KEYS.upgradeCitationObject,
    JSON.stringify(upgradeCitation)
  )
}

export const removeUpgradeCitationFromSessionStorage = () =>
  removeSessionStorageItem(SESSION_STORAGE_KEYS.upgradeCitationObject)

export const removeCitationFromSessionStorage = () =>
  removeSessionStorageItem(SESSION_STORAGE_KEYS.citationObject)

export const setPaymentDoneOnUpgradeCitationObject = (createProject: CreateProject) => {
  const upgradeCitationObject = getUpgradeCitationsFromSessionStorage()
  if (!upgradeCitationObject) return

  upgradeCitationObject.route.isPaymentDone = true
  if (!upgradeCitationObject.route.projectId) {
    // no project id
    createProject({ shouldFetchCitations: true }) // creates project & triggers fetch citations
  }
  setUpgradeCitationInSessionStorage(upgradeCitationObject)
}

export const setPaymentDoneOnCitationObject = (createProject: CreateProject) => {
  const upgradeCitationObject = getCitationsFromSessionStorage()
  if (!upgradeCitationObject) return

  upgradeCitationObject.route.isPaymentDone = true
  if (!upgradeCitationObject.route.projectId) {
    // no project id
    createProject({ shouldFetchCitations: true }) // creates project & triggers fetch citations
  }
  setSessionStorageItem(
    SESSION_STORAGE_KEYS.citationObject,
    JSON.stringify(upgradeCitationObject)
  )
}

export const hasUpgradeCitationObject = () => !!getUpgradeCitationsFromSessionStorage()
export const hasCitationObject = () => !!getCitationsFromSessionStorage()
